import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import {
  INTERVIEW_ATTENDANCE,
  INTERVIEW_ATTENDANCE_SAVE_VALUES,
  INTERVIEW_TYPE,
  RescheduleReason,
  SUBMISSIONS_CATEGORIES,
  SUBMISSIONS_STATUSES,
} from '@shared/constants';

import { SubmissionsService } from '../../../../../careers/submissions/services/submissions.service';
import { ReferenceService } from '../../../../shared/services/reference.static.service';

@Component({
  selector: 'el-careers-email-redirect-page',
  templateUrl: './technical-interview-page.component.html',
  styleUrls: ['./technical-interview-page.component.scss'],
})
export class TechnicalInterviewPageComponent implements OnInit, OnDestroy {
  attendanceMapping = {
    [INTERVIEW_ATTENDANCE.YES]: INTERVIEW_ATTENDANCE_SAVE_VALUES.YES,
    [INTERVIEW_ATTENDANCE.NO]: INTERVIEW_ATTENDANCE_SAVE_VALUES.NO,
    [INTERVIEW_ATTENDANCE.RESCHEDULE]:
      INTERVIEW_ATTENDANCE_SAVE_VALUES.RESCHEDULE,
  };

  isHrInterview = false;
  private destroy$ = new Subject<void>();

  isTechnicalInterviewPageLoading = false;
  isRescheduleFieldsRequired = false;
  isToggleRescheduleFields = false;
  isLoading = true;
  INTERVIEW_ATTENDANCE = INTERVIEW_ATTENDANCE;
  rescheduleReason: { key: string; value: string };
  rescheduleReasonsArray: RescheduleReason[] = [];
  hasPreviousResponse = false;
  technicalInterviewForm = new FormGroup({
    attendance: new FormControl('', Validators.required),
    rescheduleReason: new FormControl(''),
    rescheduleDescription: new FormControl(''),
  });
  constructor(
    private referenceService: ReferenceService,
    private submissionsService: SubmissionsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.getSubmissionRescheduleReferences();
    this.checkExistingResponse();

    const interviewType = this.route.snapshot.data['type'];
    this.isHrInterview = interviewType === INTERVIEW_TYPE.HR;
  }

  toggleRescheduleFields(toggle: boolean) {
    if (toggle) {
      this.isToggleRescheduleFields = true;
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }, 0);
    } else {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 0);
      this.isToggleRescheduleFields = false;
    }
  }

  setAttendance(attendance: INTERVIEW_ATTENDANCE) {
    const saveValue = this.attendanceMapping[attendance];
    this.technicalInterviewForm.controls.attendance.setValue(saveValue);
    if (attendance === INTERVIEW_ATTENDANCE.RESCHEDULE) {
      this.toggleRescheduleFields(true);
      this.technicalInterviewForm
        .get('rescheduleReason')
        ?.setValidators(Validators.required);
      this.technicalInterviewForm
        .get('rescheduleDescription')
        ?.setValidators(Validators.required);
    } else {
      this.toggleRescheduleFields(false);
      this.technicalInterviewForm.get('rescheduleReason')?.clearValidators();
      this.technicalInterviewForm
        .get('rescheduleDescription')
        ?.clearValidators();
    }
    this.technicalInterviewForm
      .get('rescheduleReason')
      ?.updateValueAndValidity();
    this.technicalInterviewForm
      .get('rescheduleDescription')
      ?.updateValueAndValidity();
  }

  getSubmissionRescheduleReferences() {
    this.referenceService
      .getSubmissionCategoryByName(SUBMISSIONS_CATEGORIES.RESCHEDULE_REASONS)
      .then((data) => {
        const referenceId = data._id.toString();
        this.referenceService
          .getSubmissionReferences(referenceId)
          .then((data) => {
            data.forEach((category) => {
              this.rescheduleReasonsArray.push({
                key: category._id.toString(),
                // referenceId is saved in the form control
                value: category.name,
              });
            });

            const othersReason = this.rescheduleReasonsArray.find(
              (reason) =>
                reason.value.toLowerCase() === 'others' ||
                reason.value.toLowerCase() === 'other'
            );

            if (othersReason) {
              this.rescheduleReasonsArray = this.rescheduleReasonsArray.filter(
                (reason) =>
                  reason.value.toLowerCase() !== 'others' &&
                  reason.value.toLowerCase() !== 'other'
              );

              this.rescheduleReasonsArray.push(othersReason);
            }
          });
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onSubmit() {
    this.isLoading = true;
    const formData = this.technicalInterviewForm.value;
    const submissionId = this.route.snapshot.paramMap.get('id');

    // Clear reschedule fields if attendance is YES or NO
    if (
      formData.attendance === INTERVIEW_ATTENDANCE_SAVE_VALUES.YES ||
      formData.attendance === INTERVIEW_ATTENDANCE_SAVE_VALUES.NO
    ) {
      formData.rescheduleReason = undefined;
      formData.rescheduleDescription = undefined;
    }

    if (submissionId) {
      const updateMethod = this.isHrInterview
        ? this.submissionsService.updateSubmissionResponseHR
        : this.submissionsService.updateSubmissionResponse;

      updateMethod
        .call(this.submissionsService, submissionId, formData)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.router.navigate(['thank-you'], {
              queryParams: {
                title: 'Thank you',
                sub_title: 'Your response has been recorded successfully.',
              },
            });
          },
          (error) => {
            console.error('Error updating submission response:', error);
            this.isLoading = false;
          }
        );
    } else {
      console.error('Submission ID not found in route');
      this.isLoading = false;
    }
  }

  checkExistingResponse() {
    const submissionId = this.route.snapshot.paramMap.get('id');
    if (submissionId) {
      this.submissionsService
        .getPublicSingleSubmission(submissionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (submission) => {
            //TODO @Thishan - Handle the instance when the interview has been CANCELED

            // Filter technical interview details to include only those with start_time and end_time
            const arrangedInterviews = this.isHrInterview
              ? submission.data.hr_interview_details?.filter(
                  (detail) => detail.start_time && detail.end_time
                )
              : submission.data.technical_interview_details?.filter(
                  (detail) => detail.start_time && detail.end_time
                );

            // Find the latest arranged interview detail
            const lastInterviewDetail = arrangedInterviews?.reduce(
              (latest, detail) => {
                return new Date(detail.last_modified_on) >
                  new Date(latest.last_modified_on)
                  ? detail
                  : latest;
              },
              arrangedInterviews[0]
            );

            const hasRescheduleResponse =
              lastInterviewDetail &&
              lastInterviewDetail.rescheduleReason &&
              lastInterviewDetail.rescheduleDescription;

            if (
              submission.data.status ===
              SUBMISSIONS_STATUSES.PENDING_CONFIRMATION_TECHNICAL
            ) {
              this.submissionsService.updateSubmissionResponse;
            } else {
              const checkPreviousResponse = this.isHrInterview
                ? submission.data.hr_interview_accepting_details ||
                  submission.data.hr_interview_rejecting_details
                : submission.data.accepting_details ||
                  submission.data.rejecting_details;
              this.hasPreviousResponse = !!checkPreviousResponse;
            }

            if (hasRescheduleResponse || this.hasPreviousResponse) {
              this.router.navigate(['thank-you'], {
                queryParams: {
                  title: 'Response Already Recorded',
                  sub_title: 'You have already given your response.',
                },
              });
            } else {
              this.isLoading = false;
            }
          },
          (error) => {
            console.error('Error fetching submission:', error);
            this.isLoading = false;
          }
        );
    } else {
      console.error('Submission ID not found in route');
      this.isLoading = false;
    }
  }
}
